/* ============================================================
   Re Leve IA — Estilos principais
   Paleta: verde (#2E7D32, #4CAF50, #C8E6C9), branco, cinza claro
   ============================================================ */

/* Reset e base */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

:root {
  --verde-escuro: #2E7D32;
  --verde-medio: #4CAF50;
  --verde-claro: #C8E6C9;
  --verde-fundo: #F1F8E9;
  --branco: #FFFFFF;
  --cinza-claro: #F5F5F5;
  --cinza-medio: #E0E0E0;
  --cinza-texto: #616161;
  --cinza-escuro: #212121;
  --amarelo-alerta: #FFF8E1;
  --amarelo-borda: #FFD54F;
  --radius: 12px;
  --sombra: 0 2px 8px rgba(0,0,0,0.08);
  --sombra-hover: 0 4px 16px rgba(46,125,50,0.15);
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 14px;
  color: var(--cinza-escuro);
  background: var(--verde-fundo);
}

body {
  min-height: 100vh;
  background: var(--verde-fundo);
}

/* Layout principal */
.layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  background: var(--verde-escuro);
  color: var(--branco);
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-logo {
  font-size: 2rem;
  line-height: 1;
}

.header-title {
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--branco);
}

.header-subtitle {
  font-size: 0.75rem;
  color: var(--verde-claro);
  margin-top: 0.1rem;
}

.header-badge {
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--verde-claro);
}

/* Main content */
.main-content {
  flex: 1;
  padding: 1.5rem 2rem;
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Footer */
.footer {
  background: var(--verde-escuro);
  color: var(--verde-claro);
  text-align: center;
  padding: 0.75rem;
  font-size: 0.7rem;
  letter-spacing: 0.02em;
}

/* Cards */
.card {
  background: var(--branco);
  border-radius: var(--radius);
  padding: 1.25rem;
  box-shadow: var(--sombra);
  border: 1px solid var(--cinza-medio);
  margin-bottom: 0.75rem;
}

.card--empty {
  background: var(--cinza-claro);
  border: 1.5px dashed var(--cinza-medio);
}

.card-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--verde-escuro);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--verde-claro);
}

.card-placeholder-text {
  font-size: 0.78rem;
  color: var(--cinza-texto);
  line-height: 1.5;
  font-style: italic;
}

/* Três colunas */
.three-columns {
  display: grid;
  grid-template-columns: 260px 1fr 280px;
  gap: 1.25rem;
  align-items: start;
}

.column-heading {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--verde-escuro);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 0.75rem;
  padding-left: 0.25rem;
  border-left: 3px solid var(--verde-medio);
  padding-left: 0.5rem;
}

/* Lista de clientes */
.customer-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.customer-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1.5px solid var(--cinza-medio);
  background: var(--cinza-claro);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  width: 100%;
}

.customer-btn:hover {
  border-color: var(--verde-medio);
  background: var(--verde-fundo);
}

.customer-btn--active {
  border-color: var(--verde-escuro);
  background: var(--verde-claro);
}

.customer-name {
  font-weight: 600;
  font-size: 0.82rem;
  color: var(--cinza-escuro);
}

/* Tags de segmento */
.segment-tag {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.segment-tag--campeã,
.segment-tag--campeão { background: #E8F5E9; color: #1B5E20; }
.segment-tag--leal    { background: #E3F2FD; color: #0D47A1; }
.segment-tag--em-risco { background: #FFF3E0; color: #E65100; }
.segment-tag--perdido  { background: #FFEBEE; color: #B71C1C; }
.segment-tag--nova,
.segment-tag--novo    { background: #F3E5F5; color: #4A148C; }

/* Perfil do cliente */
.profile-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.profile-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--verde-escuro);
  color: var(--branco);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.profile-name {
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.profile-stats {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.stat {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--cinza-medio);
}

.stat-label { color: var(--cinza-texto); }
.stat-value { font-weight: 700; color: var(--verde-escuro); }

/* Chat */
.chat-card {
  display: flex;
  flex-direction: column;
}

.chat-customer-tag {
  font-weight: 400;
  color: var(--verde-medio);
  font-style: italic;
  text-transform: none;
  font-size: 0.78rem;
  letter-spacing: 0;
}

.chat-messages {
  min-height: 300px;
  max-height: 420px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.5rem 0;
  margin-bottom: 0.75rem;
}

.chat-input-row {
  display: flex;
  gap: 0.5rem;
}

.chat-input {
  flex: 1;
  padding: 0.55rem 0.75rem;
  border: 1.5px solid var(--cinza-medio);
  border-radius: 8px;
  font-size: 0.82rem;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}

.chat-input:focus {
  border-color: var(--verde-medio);
}

/* Bolhas de mensagem */
.bubble-row {
  display: flex;
  align-items: flex-end;
  gap: 0.4rem;
}

.bubble-row--bot  { justify-content: flex-start; }
.bubble-row--user { justify-content: flex-end; }

.bubble-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--verde-escuro);
  color: var(--branco);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.58rem;
  font-weight: 700;
  flex-shrink: 0;
}

.bubble {
  max-width: 75%;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  font-size: 0.82rem;
  line-height: 1.45;
}

.bubble--bot {
  background: var(--verde-claro);
  color: var(--cinza-escuro);
  border-bottom-left-radius: 4px;
}

.bubble--user {
  background: var(--verde-escuro);
  color: var(--branco);
  border-bottom-right-radius: 4px;
}

.bubble-text { margin-bottom: 0.2rem; }

.bubble-time {
  font-size: 0.65rem;
  opacity: 0.6;
  display: block;
  text-align: right;
}

/* Botão primário */
.btn-primary {
  background: var(--verde-escuro);
  color: var(--branco);
  border: none;
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
}

.btn-primary:hover {
  background: var(--verde-medio);
}

/* Kit preview */
.kit-preview-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.75rem;
}

.kit-preview-item {
  display: flex;
  justify-content: space-between;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  font-size: 0.78rem;
}

.kit-preview-item--disabled {
  background: var(--cinza-claro);
  color: var(--cinza-texto);
  border: 1.5px dashed var(--cinza-medio);
}

.kit-preview-price {
  font-weight: 700;
  color: var(--verde-escuro);
}

/* Guardrails */
.guardrail-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 0.5rem;
}

.guardrail-item {
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.guardrail-item--pending {
  background: var(--amarelo-alerta);
  border: 1px solid var(--amarelo-borda);
  color: #5D4037;
}

.guardrail-item--triggered {
  background: #FFEBEE;
  border: 1px solid #EF9A9A;
  color: #B71C1C;
}

.guardrail-item--ok {
  background: var(--verde-fundo);
  border: 1px solid var(--verde-claro);
  color: var(--verde-escuro);
}

/* Acompanhamento calórico */
.card-disclaimer {
  font-size: 0.68rem;
  color: #E65100;
  background: #FFF3E0;
  border: 1px solid #FFCC80;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  margin-bottom: 0.6rem;
}

.calorie-summary {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.calorie-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  padding: 0.3rem 0;
  border-bottom: 1px solid var(--cinza-medio);
}

.calorie-value {
  font-weight: 700;
  color: var(--verde-escuro);
}

/* Kit builder */
.kit-builder-placeholder {
  background: var(--cinza-claro);
  border: 1.5px dashed var(--cinza-medio);
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.75rem;
  color: var(--cinza-texto);
  line-height: 1.6;
  margin-top: 0.5rem;
  text-align: center;
}

/* Seletor de cenários */
.scenario-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.scenario-btn {
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  border: 1.5px solid var(--cinza-medio);
  background: var(--cinza-claro);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}

.scenario-btn:hover {
  border-color: var(--verde-medio);
  background: var(--verde-fundo);
}

.scenario-btn--active {
  border-color: var(--verde-escuro);
  background: var(--verde-claro);
  color: var(--verde-escuro);
  font-weight: 700;
}

/* Resumo executivo */
.executive-summary {
  background: linear-gradient(135deg, var(--verde-escuro) 0%, #1B5E20 100%);
  color: var(--branco);
  border: none;
}

.executive-summary .card-title {
  color: var(--verde-claro);
  border-bottom-color: rgba(255,255,255,0.2);
}

.executive-tese {
  font-size: 0.88rem;
  line-height: 1.6;
  color: var(--verde-claro);
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(255,255,255,0.08);
  border-radius: 8px;
  border-left: 3px solid var(--verde-medio);
}

.executive-tese strong {
  color: var(--branco);
}

.executive-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.executive-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: var(--verde-claro);
  background: rgba(255,255,255,0.08);
  padding: 0.4rem 0.6rem;
  border-radius: 8px;
}

.executive-icon {
  font-size: 1rem;
}

/* Responsividade para notebook */
@media (max-width: 1200px) {
  .three-columns {
    grid-template-columns: 220px 1fr 240px;
  }
  .executive-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 900px) {
  .three-columns {
    grid-template-columns: 1fr;
  }
  .main-content {
    padding: 1rem;
  }
  .header {
    padding: 0.75rem 1rem;
  }
  .executive-grid {
    grid-template-columns: 1fr 1fr;
  }
}

/* ============================================================
   ETAPA 3 — Novos estilos
   ============================================================ */

/* Botão secundário */
.btn-secondary {
  background: var(--branco);
  color: var(--verde-escuro);
  border: 1.5px solid var(--verde-medio);
  border-radius: 8px;
  padding: 0.45rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.btn-secondary:hover {
  background: var(--verde-fundo);
}
.btn--small { padding: 0.3rem 0.65rem; font-size: 0.72rem; }
.btn--active { background: var(--verde-claro); }

/* Badges de contagem e status */
.badge {
  display: inline-block;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.1rem 0.45rem;
  margin-left: 0.4rem;
  vertical-align: middle;
}
.badge--count { background: var(--verde-claro); color: var(--verde-escuro); }
.badge--active { background: #FFF3E0; color: #E65100; border: 1px solid #FFCC80; }

/* Selos de dado */
.selo {
  display: inline-block;
  border-radius: 4px;
  font-size: 0.58rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  margin-left: 0.3rem;
  vertical-align: middle;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.selo--observado   { background: #E8F5E9; color: #1B5E20; border: 1px solid #A5D6A7; }
.selo--inferido    { background: #FFF8E1; color: #F57F17; border: 1px solid #FFD54F; }
.selo--simulado    { background: #E3F2FD; color: #0D47A1; border: 1px solid #90CAF9; }
.selo--anonimo     { background: #F3E5F5; color: #6A1B9A; border: 1px solid #CE93D8; }
.selo--prototipo   { background: #ECEFF1; color: #546E7A; border: 1px solid #B0BEC5; }

/* Busca e filtros do CustomerSelector */
.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1.5px solid var(--cinza-medio);
  border-radius: 8px;
  font-size: 0.8rem;
  font-family: inherit;
  margin-bottom: 0.6rem;
  outline: none;
}
.search-input:focus { border-color: var(--verde-medio); }

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.5rem;
}
.chip {
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  border: 1.5px solid var(--cinza-medio);
  background: var(--cinza-claro);
  font-size: 0.68rem;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  white-space: nowrap;
}
.chip:hover { border-color: var(--verde-medio); background: var(--verde-fundo); }
.chip--active { border-color: var(--verde-escuro); background: var(--verde-claro); color: var(--verde-escuro); font-weight: 700; }

.filter-info {
  font-size: 0.68rem;
  color: var(--cinza-texto);
  margin-bottom: 0.4rem;
}

/* CustomerSelector — botão de cliente reformulado */
.customer-list {
  max-height: 420px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-right: 2px;
}

.customer-btn {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  border: 1.5px solid var(--cinza-medio);
  background: var(--cinza-claro);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  width: 100%;
}
.customer-btn:hover { border-color: var(--verde-medio); background: var(--verde-fundo); }
.customer-btn--active { border-color: var(--verde-escuro); background: var(--verde-claro); }

.customer-btn-top { display: flex; align-items: center; justify-content: space-between; }
.customer-id { font-weight: 700; font-size: 0.78rem; color: var(--cinza-escuro); }
.customer-btn-mid { margin-top: 0.1rem; }
.customer-btn-bottom { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.1rem; }
.customer-ticket, .customer-pedidos, .customer-calorico {
  font-size: 0.65rem;
  color: var(--cinza-texto);
}

.load-more-btn {
  width: 100%;
  margin-top: 0.5rem;
  text-align: center;
}

/* Segmentos — cores */
.segment-tag--cliente-novo          { background: #E8EAF6; color: #1A237E; }
.segment-tag--cliente-fiel          { background: #E8F5E9; color: #1B5E20; }
.segment-tag--cliente-recorrente    { background: #E3F2FD; color: #0D47A1; }
.segment-tag--cliente-inativo       { background: #FBE9E7; color: #BF360C; }
.segment-tag--cliente-alto-valor    { background: #FFF8E1; color: #F57F17; }
.segment-tag--cliente-possivel-coletivo { background: #F3E5F5; color: #4A148C; }
.segment-tag--cliente-em-atencao    { background: #FFEBEE; color: #B71C1C; }
.segment-tag--cliente-baixo-ticket  { background: #ECEFF1; color: #37474F; }

/* CustomerProfileCard */
.profile-header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.profile-header-info { flex: 1; }
.profile-id { font-weight: 700; font-size: 0.88rem; margin-bottom: 0.25rem; }
.profile-selos { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-bottom: 0.25rem; align-items: center; }
.profile-anonimo { font-size: 0.65rem; color: var(--cinza-texto); }

.profile-section { margin-bottom: 0.75rem; }
.profile-section-title {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--verde-escuro);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.profile-inference-warning {
  font-size: 0.68rem;
  color: #F57F17;
  background: #FFF8E1;
  border: 1px solid #FFD54F;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  margin-bottom: 0.4rem;
}
.profile-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 0.75rem;
  padding: 0.3rem 0;
  border-bottom: 1px solid var(--cinza-medio);
  gap: 0.5rem;
}
.profile-row-label { color: var(--cinza-texto); flex-shrink: 0; min-width: 120px; }
.profile-row-value { font-weight: 600; color: var(--cinza-escuro); text-align: right; display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 0.2rem; }

/* ScenarioSelector */
.scenario-card { overflow: visible; }
.scenario-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
.scenario-header .card-title { margin-bottom: 0; border-bottom: none; padding-bottom: 0; }

.scenario-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.scenario-item {
  border: 1.5px solid var(--cinza-medio);
  border-radius: 10px;
  padding: 0.75rem;
  background: var(--cinza-claro);
  transition: all 0.15s;
}
.scenario-item:hover { border-color: var(--verde-medio); background: var(--verde-fundo); }
.scenario-item--active { border-color: var(--verde-escuro); background: var(--verde-claro); }

.scenario-item-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem; }
.scenario-id { font-weight: 700; font-size: 0.75rem; color: var(--verde-escuro); }
.scenario-tipo-tag {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  text-transform: uppercase;
}
.tag--faq          { background: #E3F2FD; color: #0D47A1; }
.tag--venda        { background: #E8F5E9; color: #1B5E20; }
.tag--relacionamento { background: #F3E5F5; color: #4A148C; }
.tag--calorico     { background: #FFF8E1; color: #F57F17; }
.tag--humano       { background: #FFEBEE; color: #B71C1C; }

.scenario-objetivo { font-weight: 600; font-size: 0.78rem; margin-bottom: 0.2rem; }
.scenario-segmento { font-size: 0.68rem; color: var(--cinza-texto); margin-bottom: 0.3rem; }
.scenario-entrada  { font-size: 0.72rem; color: var(--cinza-escuro); margin-bottom: 0.35rem; font-style: italic; }
.scenario-label-mini { font-style: normal; font-weight: 700; color: var(--verde-escuro); }

.scenario-guardrails { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-bottom: 0.35rem; }
.guardrail-badge {
  background: #FFF8E1;
  border: 1px solid #FFD54F;
  color: #F57F17;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
}

.scenario-actions { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.scenario-cta { font-size: 0.65rem; color: var(--cinza-texto); flex: 1; }

/* ChatWindow */
.chat-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
.chat-header .card-title { margin-bottom: 0; border-bottom: none; padding-bottom: 0; }

.demo-notice {
  font-size: 0.68rem;
  color: #0D47A1;
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  margin-bottom: 0.5rem;
  text-align: center;
}

.scenario-active-badge {
  font-size: 0.68rem;
  color: var(--verde-escuro);
  background: var(--verde-claro);
  border: 1px solid var(--verde-medio);
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  margin-bottom: 0.5rem;
}

/* Mensagem de cenário no chat */
.bubble--scenario { border: 1.5px solid var(--verde-medio); }
.bubble-scenario-note { font-size: 0.6rem; color: var(--verde-escuro); opacity: 0.7; margin-top: 0.3rem; font-style: italic; }

/* GuardrailPanel */
.guardrail-list-real {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 500px;
  overflow-y: auto;
}

.guardrail-card {
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  border: 1.5px solid var(--cinza-medio);
  transition: all 0.15s;
}
.guardrail-card--dim { opacity: 0.45; }
.guardrail-card--active { opacity: 1; }

.guardrail-card-header { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.3rem; }
.guardrail-id { font-weight: 700; font-size: 0.72rem; }
.guardrail-cat-tag {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  text-transform: uppercase;
}
.guardrail-human-tag { font-size: 0.6rem; font-weight: 700; padding: 0.1rem 0.4rem; border-radius: 999px; background: #FFEBEE; color: #B71C1C; border: 1px solid #EF9A9A; }

/* Cores de categoria guardrail */
.guardrail--saude           { background: #E8F5E9; border-color: #A5D6A7; }
.guardrail--personalizacao  { background: #FFF8E1; border-color: #FFD54F; }
.guardrail--catalogo        { background: #E3F2FD; border-color: #90CAF9; }
.guardrail--privacidade     { background: #F3E5F5; border-color: #CE93D8; }
.guardrail--consentimento   { background: #FBE9E7; border-color: #FFAB91; }
.guardrail--transparencia   { background: #E0F7FA; border-color: #80DEEA; }
.guardrail--comercial       { background: #FFF3E0; border-color: #FFCC80; }
.guardrail--atendimento     { background: #FCE4EC; border-color: #F48FB1; }

.guardrail-regra  { font-size: 0.73rem; line-height: 1.4; margin-bottom: 0.3rem; }
.guardrail-risco  { font-size: 0.68rem; color: var(--cinza-texto); margin-bottom: 0.25rem; }
.guardrail-resposta { font-size: 0.68rem; color: var(--verde-escuro); margin-bottom: 0.3rem; font-style: italic; }
.guardrail-flags { display: flex; gap: 0.4rem; }
.guardrail-flag { font-size: 0.62rem; padding: 0.1rem 0.4rem; border-radius: 4px; }
.flag--ok       { background: #E8F5E9; color: #1B5E20; }
.flag--atencao  { background: #FFEBEE; color: #B71C1C; }

/* KitBuilderPanel */
.kit-list { display: flex; flex-direction: column; gap: 0.4rem; max-height: 600px; overflow-y: auto; }
.kit-card {
  border: 1.5px solid var(--cinza-medio);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.15s;
}
.kit-card:hover { border-color: var(--verde-medio); }
.kit-card--selected { border-color: var(--verde-escuro); background: var(--verde-claro); }
.kit-card--suggested { border-color: #FFD54F; background: #FFFDE7; }
.kit-card--selected.kit-card--suggested { border-color: var(--verde-escuro); background: var(--verde-claro); }

.kit-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  background: inherit;
}
.kit-card-title-row { display: flex; align-items: center; gap: 0.4rem; flex: 1; }
.kit-nome { font-weight: 700; font-size: 0.8rem; }
.kit-badge-sugerido { font-size: 0.58rem; font-weight: 700; padding: 0.08rem 0.35rem; border-radius: 999px; background: #FFF8E1; color: #F57F17; border: 1px solid #FFD54F; }
.kit-badge-selecionado { font-size: 0.58rem; font-weight: 700; padding: 0.08rem 0.35rem; border-radius: 999px; background: #E8F5E9; color: #1B5E20; border: 1px solid #A5D6A7; }
.kit-toggle { font-size: 0.6rem; color: var(--cinza-texto); }

.kit-card-body { padding: 0 0.75rem 0.75rem; }
.kit-uso { font-size: 0.72rem; color: var(--cinza-texto); margin-bottom: 0.4rem; font-style: italic; }
.kit-composicao { list-style: disc; padding-left: 1rem; font-size: 0.72rem; line-height: 1.6; margin-bottom: 0.4rem; }
.kit-calorica { font-size: 0.68rem; color: var(--verde-escuro); margin-bottom: 0.3rem; }
.kit-guardrails { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-bottom: 0.35rem; }
.kit-cta { font-size: 0.72rem; color: var(--verde-escuro); font-style: italic; margin-bottom: 0.5rem; }
.kit-alerta { font-size: 0.68rem; color: #B71C1C; background: #FFEBEE; border: 1px solid #EF9A9A; border-radius: 6px; padding: 0.3rem 0.6rem; margin-bottom: 0.4rem; }
.kit-aviso { font-size: 0.65rem; color: var(--cinza-texto); font-style: italic; margin-bottom: 0.5rem; }

/* RecommendationPanel */
.rec-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem; }
.rec-item { display: flex; flex-direction: column; gap: 0.1rem; padding: 0.4rem 0.6rem; background: var(--cinza-claro); border-radius: 6px; border: 1px solid var(--cinza-medio); }
.rec-item--full { grid-column: 1 / -1; }
.rec-item--alerta { background: #FFF8E1; border-color: #FFD54F; }
.rec-item--acao { background: var(--verde-fundo); border-color: var(--verde-claro); }
.rec-label { font-size: 0.62rem; color: var(--cinza-texto); text-transform: uppercase; letter-spacing: 0.04em; }
.rec-value { font-size: 0.75rem; font-weight: 600; color: var(--cinza-escuro); }
.rec-cta { font-style: italic; color: var(--verde-escuro); }

/* ExecutiveSummary stats */
.executive-stats {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.exec-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  min-width: 60px;
}
.exec-stat-num { font-size: 1.1rem; font-weight: 700; color: var(--branco); }
.exec-stat-label { font-size: 0.6rem; color: var(--verde-claro); text-transform: uppercase; }

.executive-context {
  background: rgba(255,255,255,0.08);
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.75rem;
  border-left: 3px solid var(--verde-medio);
}
.executive-context--cenario { border-left-color: #FFD54F; }
.executive-context-title { font-size: 0.68rem; font-weight: 700; color: var(--verde-claro); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; }
.executive-context-grid { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.73rem; color: var(--verde-claro); }
.exec-obs { opacity: 0.85; font-style: italic; }
.exec-alerta { color: #FFD54F; font-weight: 700; font-size: 0.72rem; }
.executive-context-fonte { font-size: 0.62rem; color: rgba(255,255,255,0.5); margin-top: 0.4rem; font-style: italic; }
.executive-sim-aviso {
  font-size: 0.7rem;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  padding: 0.45rem 0.75rem;
  margin-bottom: 0.75rem;
  color: var(--verde-claro);
  line-height: 1.5;
}

/* ============================================================
   ETAPA 3B — Alternância de modo e site do cliente
   ============================================================ */

/* Barra de alternância de modo */
.mode-switcher {
  background: #263238;
  border-bottom: 3px solid var(--verde-medio);
  padding: 0.6rem 2rem;
}
.mode-switcher-inner {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  max-width: 1600px;
  margin: 0 auto;
}
.mode-switcher-label {
  font-size: 0.72rem;
  color: #90A4AE;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}
.mode-buttons { display: flex; gap: 0.5rem; }
.mode-btn {
  padding: 0.4rem 1rem;
  border-radius: 999px;
  border: 2px solid #546E7A;
  background: transparent;
  color: #90A4AE;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
  white-space: nowrap;
}
.mode-btn:hover { border-color: var(--verde-medio); color: var(--branco); }
.mode-btn--active {
  background: var(--verde-escuro);
  border-color: var(--verde-medio);
  color: var(--branco);
}
.mode-btn--dev.mode-btn--active {
  background: #1565C0;
  border-color: #42A5F5;
  color: var(--branco);
}
.mode-current {
  font-size: 0.68rem;
  color: #546E7A;
  font-style: italic;
}

/* ── Site do Cliente (ClientSiteView) ─────────────────────────────────────── */

.cs-site {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
}

/* Hero */
.cs-hero {
  background: linear-gradient(135deg, #1B5E20 0%, var(--verde-escuro) 60%, #388E3C 100%);
  border-radius: 0 0 24px 24px;
  padding: 3rem 2rem;
  text-align: center;
  margin-bottom: 2rem;
  margin-left: -1rem;
  margin-right: -1rem;
}
.cs-hero-inner { max-width: 640px; margin: 0 auto; }
.cs-hero-badge {
  display: inline-block;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  color: var(--verde-claro);
  font-size: 0.75rem;
  padding: 0.25rem 1rem;
  border-radius: 999px;
  margin-bottom: 1rem;
  letter-spacing: 0.03em;
}
.cs-hero-title {
  font-size: 2rem;
  font-weight: 800;
  color: var(--branco);
  line-height: 1.25;
  margin-bottom: 0.75rem;
  letter-spacing: -0.02em;
}
.cs-hero-subtitle {
  font-size: 1rem;
  color: var(--verde-claro);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

/* Botões do site do cliente */
.cs-btn {
  display: inline-block;
  padding: 0.7rem 1.5rem;
  border-radius: 999px;
  border: none;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  transition: all 0.15s;
  white-space: nowrap;
}
.cs-btn--hero { background: var(--branco); color: var(--verde-escuro); font-size: 0.9rem; padding: 0.8rem 2rem; }
.cs-btn--hero:hover { background: var(--verde-claro); }
.cs-btn--kit { background: var(--verde-escuro); color: var(--branco); font-size: 0.78rem; width: 100%; margin-top: 0.75rem; }
.cs-btn--kit:hover { background: var(--verde-medio); }
.cs-btn--promo { background: transparent; border: 2px solid var(--verde-escuro); color: var(--verde-escuro); font-size: 0.78rem; }
.cs-btn--promo:hover { background: var(--verde-fundo); }
.cs-btn--indicar { background: var(--verde-escuro); color: var(--branco); }
.cs-btn--indicar:hover { background: var(--verde-medio); }
.cs-btn--calorico { background: var(--cinza-claro); color: var(--cinza-escuro); border: 1.5px solid var(--cinza-medio); font-size: 0.8rem; }
.cs-btn--calorico:hover { background: var(--cinza-medio); }
.cs-btn--whatsapp { background: #25D366; color: var(--branco); font-size: 1rem; padding: 0.9rem 2.5rem; }
.cs-btn--whatsapp:hover { background: #20BA5A; }
.cs-btn--send { background: var(--verde-escuro); color: var(--branco); padding: 0.5rem 1rem; border-radius: 8px; font-size: 0.82rem; white-space: nowrap; }

/* Grid principal do site */
.cs-main-grid {
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  align-items: start;
}
.cs-column-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--verde-escuro);
  margin-bottom: 0.75rem;
}

/* Chat do site do cliente */
.cs-chat {
  background: var(--branco);
  border-radius: 16px;
  border: 1.5px solid var(--cinza-medio);
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(46,125,50,0.1);
}
.cs-chat-header {
  background: var(--verde-escuro);
  color: var(--branco);
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.cs-chat-avatar { font-size: 1.5rem; }
.cs-chat-name { font-weight: 700; font-size: 0.88rem; }
.cs-chat-status { font-size: 0.68rem; color: var(--verde-claro); }
.cs-chat-messages {
  padding: 1rem;
  min-height: 200px;
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: #FAFAFA;
}
.cs-bubble {
  max-width: 80%;
  padding: 0.6rem 0.9rem;
  border-radius: 12px;
  font-size: 0.85rem;
  line-height: 1.5;
}
.cs-bubble--bot {
  background: var(--verde-claro);
  color: var(--cinza-escuro);
  align-self: flex-start;
  border-bottom-left-radius: 4px;
}
.cs-bubble--user {
  background: var(--verde-escuro);
  color: var(--branco);
  align-self: flex-end;
  border-bottom-right-radius: 4px;
}
.cs-chat-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.75rem;
  border-top: 1px solid var(--cinza-medio);
  background: var(--cinza-claro);
}
.cs-quick-btn {
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1.5px solid var(--verde-medio);
  background: var(--branco);
  color: var(--verde-escuro);
  font-size: 0.75rem;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  transition: all 0.12s;
  white-space: nowrap;
}
.cs-quick-btn:hover { background: var(--verde-claro); }
.cs-chat-input-row {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
  border-top: 1px solid var(--cinza-medio);
}
.cs-chat-input {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1.5px solid var(--cinza-medio);
  border-radius: 8px;
  font-size: 0.82rem;
  font-family: inherit;
  outline: none;
}
.cs-chat-input:focus { border-color: var(--verde-medio); }

/* Seções do site */
.cs-section {
  padding: 2rem 0;
  border-top: 1px solid var(--cinza-medio);
}
.cs-section--alt { background: var(--verde-fundo); border-radius: 16px; padding: 2rem; margin: 1rem -1rem; }
.cs-section-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--verde-escuro);
  margin-bottom: 1.25rem;
  letter-spacing: -0.01em;
}

/* Grid de kits */
.cs-kits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}
.cs-kit-card {
  background: var(--branco);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1.5px solid var(--cinza-medio);
  box-shadow: var(--sombra);
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
}
.cs-kit-card:hover { box-shadow: var(--sombra-hover); border-color: var(--verde-medio); transform: translateY(-2px); }
.cs-kit-icon { font-size: 2rem; margin-bottom: 0.5rem; }
.cs-kit-name { font-weight: 800; font-size: 0.95rem; margin-bottom: 0.25rem; color: var(--cinza-escuro); }
.cs-kit-uso { font-size: 0.75rem; color: var(--cinza-texto); margin-bottom: 0.75rem; }
.cs-kit-composicao { list-style: disc; padding-left: 1rem; font-size: 0.75rem; line-height: 1.7; color: var(--cinza-escuro); flex: 1; }
.cs-kit-cal { font-size: 0.7rem; color: var(--verde-escuro); margin: 0.5rem 0; font-weight: 600; }

/* Promoções */
.cs-promos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}
.cs-promo-card {
  background: var(--branco);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1.5px solid var(--cinza-medio);
  box-shadow: var(--sombra);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  transition: all 0.15s;
  position: relative;
}
.cs-promo-card:hover { box-shadow: var(--sombra-hover); border-color: var(--verde-medio); }
.cs-promo-card--destaque { border-color: var(--verde-medio); background: var(--verde-fundo); }
.cs-promo-icon { font-size: 1.8rem; }
.cs-promo-badge {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: var(--verde-claro);
  color: var(--verde-escuro);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.cs-promo-titulo { font-weight: 700; font-size: 0.95rem; color: var(--cinza-escuro); }
.cs-promo-desc { font-size: 0.78rem; color: var(--cinza-texto); line-height: 1.5; flex: 1; }

/* Indicação */
.cs-indicacao-card {
  background: var(--branco);
  border-radius: 16px;
  padding: 1.5rem;
  border: 2px solid var(--verde-medio);
  box-shadow: var(--sombra);
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  max-width: 600px;
}
.cs-indicacao-icon { font-size: 2.5rem; flex-shrink: 0; }
.cs-indicacao-title { font-weight: 700; font-size: 1rem; margin-bottom: 0.4rem; }
.cs-indicacao-desc { font-size: 0.82rem; color: var(--cinza-texto); margin-bottom: 0.75rem; line-height: 1.5; }

/* Acompanhamento calórico */
.cs-calorico-card {
  background: var(--branco);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1.5px solid var(--cinza-medio);
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  max-width: 600px;
}
.cs-calorico-icon { font-size: 2.5rem; flex-shrink: 0; }
.cs-calorico-title { font-weight: 700; font-size: 1rem; margin-bottom: 0.4rem; }
.cs-calorico-desc { font-size: 0.82rem; color: var(--cinza-texto); margin-bottom: 0.5rem; line-height: 1.5; }
.cs-calorico-aviso {
  font-size: 0.72rem;
  color: #E65100;
  background: #FFF3E0;
  border: 1px solid #FFCC80;
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  margin-bottom: 0.75rem;
}

/* CTA final */
.cs-cta-section { text-align: center; padding: 3rem 1rem; }
.cs-cta-title { font-size: 1.6rem; font-weight: 800; color: var(--verde-escuro); margin-bottom: 0.5rem; }
.cs-cta-subtitle { font-size: 0.9rem; color: var(--cinza-texto); margin-bottom: 1.5rem; }

/* ── Janela do Desenvolvedor (DeveloperLabView) ───────────────────────────── */

.dev-lab {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.dev-lab-header {
  background: linear-gradient(135deg, #1565C0 0%, #0D47A1 100%);
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  color: var(--branco);
}
.dev-lab-title { font-size: 1.1rem; font-weight: 700; margin-bottom: 0.25rem; }
.dev-lab-subtitle { font-size: 0.75rem; color: #90CAF9; }

/* Painéis do desenvolvedor */
.dev-panel {
  background: var(--branco);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  box-shadow: var(--sombra);
  border: 1.5px solid #BBDEFB;
  margin-bottom: 0.75rem;
}
.dev-panel--controls { border-color: #E3F2FD; background: #F8FBFF; }
.dev-panel--executive { border-color: #FFD54F; background: #FFFDE7; }
.dev-panel-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1565C0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid #BBDEFB;
}

.dev-section { margin-bottom: 0.75rem; padding-bottom: 0.5rem; border-bottom: 1px dashed #E3F2FD; }
.dev-section:last-child { border-bottom: none; margin-bottom: 0; }
.dev-section-label {
  font-size: 0.65rem;
  font-weight: 700;
  color: #1565C0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.dev-info-row {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  font-size: 0.75rem;
  padding: 0.2rem 0;
}
.dev-label { color: #546E7A; min-width: 140px; flex-shrink: 0; }
.dev-value { font-weight: 600; color: var(--cinza-escuro); }
.dev-value--code { font-family: monospace; font-size: 0.8rem; color: #1565C0; }
.dev-value--fonte { font-style: italic; font-size: 0.7rem; color: var(--cinza-texto); }

.dev-badge {
  display: inline-block;
  font-size: 0.58rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  background: #E3F2FD;
  color: #0D47A1;
  border: 1px solid #90CAF9;
  text-transform: uppercase;
}
.dev-badge--inferido { background: #FFF8E1; color: #F57F17; border-color: #FFD54F; }

.dev-alert {
  font-size: 0.72rem;
  color: #6A1B9A;
  background: #F3E5F5;
  border: 1px solid #CE93D8;
  border-radius: 6px;
  padding: 0.35rem 0.6rem;
  margin-top: 0.35rem;
}

.dev-memoria-item {
  display: flex;
  gap: 0.5rem;
  font-size: 0.72rem;
  padding: 0.2rem 0;
  border-bottom: 1px solid var(--cinza-medio);
}
.dev-memoria-tipo { font-weight: 700; color: #1565C0; flex-shrink: 0; }
.dev-memoria-texto { color: var(--cinza-texto); }

.dev-cenario-item { display: flex; gap: 0.5rem; font-size: 0.72rem; padding: 0.2rem 0; }
.dev-cenario-id { font-weight: 700; color: #1565C0; flex-shrink: 0; }
.dev-cenario-obj { color: var(--cinza-texto); }

.dev-empty { font-size: 0.78rem; color: var(--cinza-texto); font-style: italic; }

/* Controles de simulação */
.dev-sim-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.75rem 0;
}
.dev-sim-btn {
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1.5px solid #BBDEFB;
  background: var(--cinza-claro);
  font-size: 0.72rem;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  color: var(--cinza-escuro);
}
.dev-sim-btn:hover { border-color: #42A5F5; background: #E3F2FD; }
.dev-sim-btn--active { background: #1565C0; border-color: #1565C0; color: var(--branco); font-weight: 700; }

.dev-sim-result {
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.73rem;
  color: #0D47A1;
}
.dev-sim-result-label { margin-bottom: 0.2rem; }
.dev-sim-note { color: #42A5F5; font-size: 0.68rem; }

/* Leitura executiva do dev */
.dev-exec-aviso {
  font-size: 0.72rem;
  background: #FFF8E1;
  border: 1px solid #FFD54F;
  border-radius: 6px;
  padding: 0.45rem 0.75rem;
  margin-bottom: 0.75rem;
  color: #5D4037;
  line-height: 1.5;
}
.dev-exec-row {
  display: flex;
  gap: 0.5rem;
  font-size: 0.75rem;
  padding: 0.25rem 0;
  border-bottom: 1px solid #FFF9C4;
  align-items: flex-start;
}
.dev-exec-row span { color: var(--cinza-texto); min-width: 160px; flex-shrink: 0; }
.dev-exec-row strong { color: var(--cinza-escuro); }

/* Preview do site do cliente no dev */
.dev-preview-section {
  background: #ECEFF1;
  border-radius: 12px;
  padding: 1.25rem;
  border: 2px dashed #90A4AE;
}
.dev-preview-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #546E7A;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.3rem;
}
.dev-preview-subtitle { font-size: 0.72rem; color: #78909C; margin-bottom: 1rem; }
.dev-preview-frame {
  border: 2px solid #90A4AE;
  border-radius: 10px;
  overflow: hidden;
  background: var(--verde-fundo);
}

/* Etiqueta de valores simulados no perfil */
.profile-sim-aviso {
  font-size: 0.65rem;
  color: #0D47A1;
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  margin-bottom: 0.4rem;
}
.profile-coletivo-aviso {
  font-size: 0.68rem;
  color: #6A1B9A;
  background: #F3E5F5;
  border: 1px solid #CE93D8;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  margin-bottom: 0.4rem;
}

/* ── ClientSitePreview ────────────────────────────────────────────────────── */

.csp-wrapper {
  border-radius: 8px;
  overflow: hidden;
  background: var(--verde-fundo);
  font-size: 0.82rem;
}
.csp-topbar {
  background: var(--verde-escuro);
  color: var(--branco);
  padding: 0.4rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.72rem;
}
.csp-topbar-brand { font-weight: 700; }
.csp-topbar-label { color: var(--verde-claro); flex: 1; }
.csp-topbar-seg {
  font-size: 0.65rem;
  background: rgba(255,255,255,0.15);
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  color: var(--verde-claro);
}
.csp-body { padding: 1rem; }
.csp-hero {
  background: linear-gradient(135deg, var(--verde-escuro) 0%, #388E3C 100%);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  margin-bottom: 1rem;
}
.csp-hero-title { font-size: 0.9rem; font-weight: 800; color: var(--branco); margin-bottom: 0.25rem; }
.csp-hero-sub { font-size: 0.72rem; color: var(--verde-claro); }
.csp-chat {
  background: var(--branco);
  border-radius: 10px;
  border: 1.5px solid var(--cinza-medio);
  overflow: hidden;
  margin-bottom: 1rem;
}
.csp-chat-header {
  background: var(--verde-escuro);
  color: var(--branco);
  padding: 0.45rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 700;
}
.csp-online { font-size: 0.65rem; color: #A5D6A7; }
.csp-messages {
  padding: 0.75rem;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
  background: #FAFAFA;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.csp-bubble {
  max-width: 85%;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  font-size: 0.78rem;
  line-height: 1.4;
}
.csp-bubble--bot { background: var(--verde-claro); color: var(--cinza-escuro); align-self: flex-start; border-bottom-left-radius: 3px; }
.csp-bubble--user { background: var(--verde-escuro); color: var(--branco); align-self: flex-end; border-bottom-right-radius: 3px; }
.csp-quick-row { display: flex; flex-wrap: wrap; gap: 0.3rem; padding: 0.5rem 0.75rem; background: var(--cinza-claro); border-top: 1px solid var(--cinza-medio); }
.csp-quick {
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  border: 1.5px solid var(--verde-medio);
  background: var(--branco);
  font-size: 0.68rem;
  cursor: pointer;
  font-family: inherit;
  color: var(--verde-escuro);
  font-weight: 600;
}
.csp-quick:hover { background: var(--verde-claro); }
.csp-kits { margin-bottom: 0.75rem; }
.csp-kits-title { font-size: 0.72rem; font-weight: 700; color: var(--verde-escuro); margin-bottom: 0.4rem; }
.csp-kits-row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.csp-kit {
  background: var(--branco);
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--cinza-medio);
  font-size: 0.7rem;
  flex: 1;
  min-width: 120px;
}
.csp-kit-name { font-weight: 700; color: var(--cinza-escuro); margin-bottom: 0.2rem; }
.csp-kit-uso { color: var(--cinza-texto); }
.csp-cta { text-align: center; margin-bottom: 0.5rem; }
.csp-cta-btn {
  background: #25D366;
  color: var(--branco);
  border: none;
  border-radius: 999px;
  padding: 0.5rem 1.25rem;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.csp-dev-note {
  font-size: 0.6rem;
  color: #90A4AE;
  text-align: center;
  font-style: italic;
  padding: 0.25rem;
  border-top: 1px dashed #CFD8DC;
  margin-top: 0.5rem;
}

/* ============================================================
   Etapa 4 — Motor determinístico do chatbot
   ============================================================ */

/* ── Consentimento (site do cliente) ───────────────────────── */
.cs-consent-buttons {
  display: flex; gap: 0.5rem; margin: 0.35rem 0 0.35rem 0.75rem; flex-wrap: wrap;
}
.cs-consent-btn {
  padding: 0.4rem 0.85rem; border-radius: 999px; border: 2px solid;
  font-size: 0.78rem; font-weight: 700; cursor: pointer; font-family: inherit;
}
.cs-consent-btn--yes { border-color: #2E7D32; background: #E8F5E9; color: #2E7D32; }
.cs-consent-btn--yes:hover { background: #C8E6C9; }
.cs-consent-btn--no  { border-color: #9E9E9E; background: #F5F5F5; color: #616161; }
.cs-consent-btn--no:hover { background: #EEEEEE; }

/* ── CTA de escalação (site do cliente) ────────────────────── */
.cs-escalate-cta { margin: 0.35rem 0 0.35rem 0.75rem; }
.cs-btn--escalate {
  display: inline-block; padding: 0.4rem 1rem; border-radius: 999px;
  background: #D32F2F; color: #fff; font-weight: 700; font-size: 0.8rem;
  text-decoration: none;
}
.cs-btn--escalate:hover { background: #B71C1C; }

/* ── CTA WhatsApp inline (site do cliente) ─────────────────── */
.cs-whatsapp-cta { margin: 0.35rem 0 0.35rem 0.75rem; }
.cs-btn--whatsapp-inline {
  display: inline-block; padding: 0.4rem 1rem; border-radius: 999px;
  background: #25D366; color: #fff; font-weight: 700; font-size: 0.8rem;
  text-decoration: none;
}
.cs-btn--whatsapp-inline:hover { background: #128C7E; }

/* ── Rastreamento da decisão (ChatWindow) ──────────────────── */
.dev-trace {
  background: #0D1117; color: #C9D1D9; border-radius: 8px;
  padding: 0.75rem 1rem; margin: 0.75rem 0; font-size: 0.75rem;
  font-family: 'Consolas', 'Courier New', monospace;
  border: 1px solid #30363D;
}
.dev-trace-title {
  font-size: 0.8rem; font-weight: 700; color: #58A6FF;
  margin-bottom: 0.6rem; font-family: inherit;
}
.dev-trace-section {
  margin-bottom: 0.4rem; line-height: 1.5; color: #C9D1D9;
}
.dev-trace-section strong { color: #58A6FF; }
.dev-trace-terms { color: #8B949E; }
.dev-trace-guardrail-active { color: #F85149; font-weight: 700; }
.dev-trace-ok { color: #3FB950; }
.dev-trace-blocked { color: #F85149; font-weight: 700; }
.dev-trace-steps { margin-top: 0.5rem; }
.dev-trace-steps ol { padding-left: 1.25rem; color: #8B949E; }
.dev-trace-steps li { margin-bottom: 0.2rem; }
.dev-trace ul { padding-left: 1.25rem; color: #8B949E; }
.dev-trace ul li { margin-bottom: 0.2rem; }

/* ── Meta da mensagem (ChatWindow) ─────────────────────────── */
.chat-msg-meta {
  display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap;
  padding: 0.1rem 0.75rem 0.4rem; font-size: 0.68rem;
}
.chat-meta-intent {
  background: #E3F2FD; color: #1565C0; border-radius: 999px;
  padding: 0.1rem 0.5rem; font-weight: 600;
}
.chat-meta-conf { color: #90A4AE; }
.chat-meta-guardrail {
  background: #FFF3E0; color: #E65100; border-radius: 999px;
  padding: 0.1rem 0.5rem; font-weight: 600;
}

/* ── GuardrailPanel — acionado ─────────────────────────────── */
.guardrail-triggered-tag {
  background: #FFECB3; color: #E65100; border-radius: 999px;
  padding: 0.1rem 0.5rem; font-size: 0.68rem; font-weight: 700;
}
.guardrail-commercial-blocked {
  background: #FBE9E7; color: #BF360C; border-radius: 6px;
  padding: 0.35rem 0.6rem; font-size: 0.72rem; font-weight: 600;
  margin-top: 0.35rem;
}
.guardrail-escalate-notice {
  background: #FFF3E0; color: #E65100; border-radius: 6px;
  padding: 0.35rem 0.6rem; font-size: 0.72rem; font-weight: 600;
  margin-top: 0.25rem;
}
.guardrail-none-active { color: #388E3C; font-weight: 600; }
.guardrail-active-summary {
  background: #FFF8E1; border: 1px solid #FFD54F; border-radius: 6px;
  padding: 0.4rem 0.6rem; font-size: 0.78rem; margin-bottom: 0.5rem;
}
.guardrail-block-badge { color: #BF360C; font-weight: 700; }
.guardrail-escalate-badge { color: #E65100; font-weight: 700; }

/* ── RecommendationPanel — motor ───────────────────────────── */
.badge--motor {
  background: #3949AB; color: #fff; border-radius: 999px;
  padding: 0.1rem 0.45rem; font-size: 0.68rem; font-weight: 700;
  margin-left: 0.4rem;
}
.rec-blocked {
  background: #FBE9E7; color: #BF360C; border-radius: 6px;
  padding: 0.4rem 0.75rem; font-size: 0.8rem; font-weight: 600;
  margin-bottom: 0.5rem;
}
.rec-kit-name { font-weight: 700; color: #2E7D32; }
.rec-composicao {
  list-style: disc; padding-left: 1.2rem; font-size: 0.75rem;
  color: #616161; margin-top: 0.2rem;
}
.rec-fonte { margin-top: 0.25rem; }
.rec-value--fonte { font-size: 0.68rem; color: #90A4AE; font-style: italic; }

/* ============================================================
   Etapa 5 — Consentimento, CalorieTracker, Restrições, Aprendizados
   ============================================================ */

/* ── Status badges de consentimento ── */
.calorie-status-badge {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.calorie-status--granted    { background: #E8F5E9; color: #2E7D32; border: 1px solid #A5D6A7; }
.calorie-status--denied     { background: #FFEBEE; color: #C62828; border: 1px solid #EF9A9A; }
.calorie-status--requested  { background: #FFF8E1; color: #F57F17; border: 1px solid #FFD54F; }
.calorie-status--not_requested { background: #F5F5F5; color: #757575; border: 1px solid #E0E0E0; }

/* ── CalorieTrackerPanel — modo cliente ── */
.calorie-card {
  border-radius: var(--radius);
  padding: 1.25rem;
  border: 1.5px solid var(--cinza-medio);
  background: var(--branco);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.calorie-card--active  { border-color: #A5D6A7; background: #F9FBE7; }
.calorie-card--inactive{ border-color: var(--cinza-medio); }

.calorie-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.calorie-icon  { font-size: 1.75rem; }
.calorie-title { font-size: 1rem; font-weight: 700; color: var(--verde-escuro); }
.calorie-desc  { font-size: 0.82rem; color: var(--cinza-texto); }
.calorie-disclaimer {
  font-size: 0.72rem;
  color: #F57F17;
  background: #FFF8E1;
  border: 1px solid #FFE082;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
}
.calorie-denied-note {
  font-size: 0.75rem;
  color: #757575;
  background: #F5F5F5;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
}

/* Resumo calórico no modo cliente */
.calorie-summary {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.calorie-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  padding: 0.3rem 0;
  border-bottom: 1px dashed var(--cinza-medio);
}
.calorie-value { font-weight: 700; color: var(--verde-escuro); }

.calorie-meals-list {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-top: 0.25rem;
}
.calorie-meal-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--cinza-texto);
  padding: 0.15rem 0.5rem;
  background: var(--cinza-claro);
  border-radius: 4px;
}
.calorie-meal-kcal { font-weight: 600; color: var(--verde-escuro); }

.calorie-suggestion {
  font-size: 0.8rem;
  background: #E8F5E9;
  border-left: 3px solid var(--verde-medio);
  padding: 0.4rem 0.75rem;
  border-radius: 0 6px 6px 0;
  color: var(--verde-escuro);
}

/* ── CalorieTrackerPanel — modo desenvolvedor ── */
.dev-calorie-notice {
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  color: #1565C0;
  margin-bottom: 0.75rem;
}
.dev-calorie-cleared {
  background: #E8F5E9;
  border: 1px solid #A5D6A7;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  color: #2E7D32;
  margin-bottom: 0.5rem;
}
.dev-calorie-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}
.dev-clear-btn {
  background: #FFEBEE;
  border: 1.5px solid #EF9A9A;
  color: #C62828;
  padding: 0.4rem 0.85rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
}
.dev-clear-btn:hover { background: #FFCDD2; }

/* ── Painel Aprendizados da sessão ── */
.dev-panel--learnings {
  background: #FAFAFA;
  border: 1.5px solid #B0BEC5;
  border-radius: var(--radius);
  padding: 1.25rem;
  margin-bottom: 1.5rem;
}
.dev-learnings-notice {
  background: #E3F2FD;
  border: 1px solid #90CAF9;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  color: #1565C0;
  margin-bottom: 1rem;
}
/* ============================================================
   Etapa 6 — Jornada Comercial, Checkout Simulado, Badges
   ============================================================ */

/* Badges globais */
.cs-badge { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.72rem; font-weight: 600; padding: 0.2rem 0.55rem; border-radius: 20px; }
.cs-badge--confirmed { background: #E8F5E9; color: #2E7D32; border: 1px solid #A5D6A7; }
.cs-badge--calorie   { background: #E3F2FD; color: #1565C0; border: 1px solid #90CAF9; }
.cs-badge--whatsapp  { background: #F3E5F5; color: #6A1B9A; border: 1px solid #CE93D8; }
.cs-badge--meal      { background: #FFF8E1; color: #E65100; border: 1px solid #FFD54F; }
.cs-badge-row        { display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 0.5rem 0.5rem 0; }

/* Card de checkout simulado */
.cs-checkout-card {
  margin: 0.75rem;
  background: #E8F5E9;
  border: 1.5px solid #A5D6A7;
  border-radius: 12px;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
}
.cs-checkout-header  { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
.cs-checkout-icon    { font-size: 1.5rem; }
.cs-checkout-title   { font-weight: 700; color: var(--verde-escuro); font-size: 0.9rem; }
.cs-checkout-kit     { font-weight: 700; color: #1B5E20; font-size: 1rem; margin-bottom: 0.5rem; }
.cs-checkout-summary { background: white; border-radius: 8px; padding: 0.75rem; margin-bottom: 0.75rem; }
.cs-checkout-summary-text { font-size: 0.82rem; color: #333; line-height: 1.5; }
.cs-checkout-badge-row { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.75rem; }
.cs-checkout-no-consent { background: #FFF8E1; border-radius: 8px; padding: 0.6rem; margin-bottom: 0.75rem; }
.cs-checkout-no-consent-text { font-size: 0.8rem; color: #795548; margin-bottom: 0.5rem; }
.cs-checkout-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.cs-checkout-disclaimer { font-size: 0.7rem; color: #9E9E9E; margin-top: 0.6rem; font-style: italic; }

/* Botão confirmar kit no chat */
.cs-kit-confirm-row { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0.4rem 0.5rem; }
.cs-btn--confirm-kit { background: #2E7D32; color: white; border: none; border-radius: 20px; padding: 0.4rem 1rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: background 0.2s; }
.cs-btn--confirm-kit:hover { background: #1B5E20; }
.cs-btn--ghost-sm { background: transparent; color: var(--verde-escuro); border: 1px solid var(--verde-escuro); border-radius: 20px; padding: 0.4rem 0.8rem; font-size: 0.78rem; cursor: pointer; }
.cs-btn--ghost    { background: transparent; color: #9E9E9E; border: 1px solid #E0E0E0; border-radius: 20px; padding: 0.4rem 0.8rem; font-size: 0.78rem; cursor: pointer; }
.cs-btn--secondary { background: #E3F2FD; color: #1565C0; border: none; border-radius: 8px; padding: 0.4rem 0.9rem; font-size: 0.78rem; font-weight: 600; cursor: pointer; }
.cs-btn--meal-register { background: #FFF8E1; color: #E65100; border: 1px solid #FFD54F; border-radius: 20px; padding: 0.4rem 1rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; }

/* Ações contextuais */
.cs-contextual-actions { display: flex; gap: 0.5rem; padding: 0.4rem 0.75rem; flex-wrap: wrap; border-top: 1px solid var(--cinza-medio); }

/* Developer Lab — Jornada Comercial */
.dev-panel--journey      { background: #F3E5F5; border-left: 4px solid #9C27B0; }
.dev-journey-grid        { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem; margin-bottom: 1rem; }
.dev-journey-block       { background: white; border-radius: 8px; padding: 0.75rem; }
.dev-journey-block--full { grid-column: 1 / -1; }
.dev-journey-action      { font-size: 0.82rem; color: #4A148C; font-weight: 600; margin: 0.3rem 0; }
.journey-stage-badge     { display: inline-block; background: #9C27B0; color: white; border-radius: 20px; padding: 0.25rem 0.75rem; font-size: 0.78rem; font-weight: 700; }

/* Eventos comerciais */
.dev-commercial-events { display: flex; flex-direction: column; gap: 0.25rem; }
.dev-commercial-event  { display: flex; align-items: center; gap: 0.5rem; font-size: 0.78rem; padding: 0.25rem 0.5rem; background: white; border-radius: 4px; border-left: 3px solid #9C27B0; }
.dev-event-type        { font-weight: 700; color: #6A1B9A; }
.dev-event-source      { color: #9E9E9E; font-size: 0.72rem; }
.dev-trigger-item      { font-size: 0.8rem; color: #E65100; padding: 0.2rem 0; }
.dev-whatsapp-preview  { background: #DCF8C6; border-radius: 8px; padding: 0.75rem; font-size: 0.82rem; color: #1B5E20; line-height: 1.5; white-space: pre-line; }
.dev-badge--tipo       { background: #EDE7F6; color: #4527A0; border: none; border-radius: 4px; font-size: 0.7rem; padding: 0.15rem 0.5rem; }

/* Impacto comercial */
.dev-impact-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 0.5rem; }
.dev-impact-item { background: white; border-radius: 8px; padding: 0.6rem; display: flex; justify-content: space-between; align-items: center; }
.dev-impact-label { font-size: 0.8rem; font-weight: 600; }
.dev-impact-level { font-size: 0.72rem; font-weight: 700; padding: 0.2rem 0.5rem; border-radius: 20px; }
.dev-impact-level--alto     { background: #E8F5E9; color: #2E7D32; }
.dev-impact-level--médio    { background: #FFF8E1; color: #E65100; }
.dev-impact-level--baixo    { background: #FAFAFA; color: #9E9E9E; }
.dev-impact-level--crítico  { background: #FFEBEE; color: #C62828; }
.dev-impact-level--necessário { background: #E3F2FD; color: #1565C0; }

/* RecommendationPanel — Etapa 6 */
.rec-action-buttons  { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.5rem; }
.rec-btn             { border: none; border-radius: 20px; padding: 0.4rem 1rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; transition: background 0.2s; }
.rec-btn--confirm    { background: #2E7D32; color: white; }
.rec-btn--confirm:hover { background: #1B5E20; }
.rec-btn--confirm:disabled { background: #A5D6A7; cursor: default; }
.rec-btn--wa         { background: #128C7E; color: white; }
.rec-btn--wa:hover   { background: #075E54; }
.rec-confirmed-kit   { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.75rem; padding: 0.5rem; background: #E8F5E9; border-radius: 8px; }
.rec-whatsapp-box    { background: #DCF8C6; border-radius: 8px; padding: 0.75rem; }
.rec-wa-label        { font-size: 0.72rem; color: #075E54; font-weight: 700; margin-bottom: 0.4rem; }
.rec-wa-text         { font-size: 0.82rem; color: #1B5E20; white-space: pre-line; line-height: 1.5; }
.rec-wa-disclaimer   { font-size: 0.68rem; color: #9E9E9E; margin-top: 0.4rem; font-style: italic; }
.rec-crosssell-list  { display: flex; flex-wrap: wrap; gap: 0.3rem; margin-top: 0.3rem; }
.rec-crosssell-tag   { background: #FFF8E1; border: 1px solid #FFD54F; color: #E65100; border-radius: 20px; padding: 0.2rem 0.6rem; font-size: 0.75rem; }
.badge--journey      { background: #EDE7F6; color: #4527A0; border-radius: 4px; font-size: 0.7rem; padding: 0.15rem 0.5rem; margin-left: 0.3rem; }

/* ExecutiveSummary — Etapa 6 */
.executive-context--etapa6  { border-left-color: #9C27B0; }
.exec-journey-stage          { font-size: 0.85rem; margin-bottom: 0.75rem; }
.exec-commercial-grid        { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.75rem; margin-top: 0.5rem; }
.exec-commercial-item        { background: white; border-radius: 8px; padding: 0.75rem; }
.exec-commercial-item strong { display: block; margin-bottom: 0.25rem; font-size: 0.85rem; }
.exec-commercial-item p      { font-size: 0.78rem; color: #616161; line-height: 1.4; margin-bottom: 0.4rem; }
.exec-commercial-icon        { font-size: 1.1rem; display: block; margin-bottom: 0.3rem; }
.exec-potencial              { font-size: 0.72rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: 20px; }
.exec-potencial--alto        { background: #E8F5E9; color: #2E7D32; }
.exec-potencial--médio       { background: #FFF8E1; color: #E65100; }
.exec-potencial--baixo       { background: #FAFAFA; color: #9E9E9E; }
.exec-potencial--crítico     { background: #FFEBEE; color: #C62828; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

/* ============================================================ */
.dev-learnings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.dev-learning-block {
  background: var(--branco);
  border: 1px solid var(--cinza-medio);
  border-radius: 8px;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.dev-learning-block-title {
  font-size: 0.7rem;
  font-weight: 700;
  color: #607D8B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.dev-learning-detail {
  font-size: 0.75rem;
  color: var(--cinza-texto);
}
.dev-learning-restriction {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: #C62828;
  font-weight: 600;
}
.dev-learning-event {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.2rem 0;
  border-bottom: 1px dashed var(--cinza-medio);
}
.dev-learning-event-type {
  font-size: 0.7rem;
  font-weight: 700;
  color: #1565C0;
  font-family: monospace;
}
.dev-learning-event-desc {
  font-size: 0.7rem;
  color: var(--cinza-texto);
}
.dev-learnings-actions {
  display: flex;
  justify-content: flex-end;
}

/* ── Trace Etapa 5 — consentimento e restrições ── */
.dev-trace-section--consent {
  background: #E8F5E9;
  border-left: 3px solid #4CAF50;
  padding: 0.35rem 0.65rem;
  border-radius: 0 4px 4px 0;
}
.dev-trace-section--restriction {
  background: #FFF8E1;
  border-left: 3px solid #FFB300;
  padding: 0.35rem 0.65rem;
  border-radius: 0 4px 4px 0;
}
.trace-consent-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}
.trace-consent--granted   { background: #C8E6C9; color: #2E7D32; }
.trace-consent--denied    { background: #FFCDD2; color: #C62828; }
.trace-consent--requested { background: #FFF9C4; color: #F57F17; }
.trace-consent--not_requested { background: #F5F5F5; color: #757575; }
.trace-event-chip {
  display: inline-block;
  background: #E3F2FD;
  color: #1565C0;
  font-size: 0.68rem;
  font-family: monospace;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  margin-right: 0.25rem;
}

/* ── RecommendationPanel — Etapa 5 ── */
.rec-collective-note {
  background: #EDE7F6;
  color: #4527A0;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
}
.rec-restriction-note {
  background: #FFF8E1;
  color: #E65100;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
  border-left: 3px solid #FF9800;
}
.rec-consent-suggestion {
  background: #E8F5E9;
  color: #1B5E20;
  border-radius: 6px;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
}
.rec-value--active   { color: #2E7D32; font-weight: 700; }
.rec-value--inactive { color: #9E9E9E; }
.badge--calorie {
  background: #E8F5E9;
  color: #2E7D32;
  border: 1px solid #A5D6A7;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.65rem;
  font-weight: 700;
  margin-left: 0.4rem;
  text-transform: uppercase;
}

/* ── ExecutiveSummary — Etapa 5 ── */
.executive-context--etapa5 {
  background: #F3E5F5;
  border: 1px solid #CE93D8;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}
.exec-consent-badge {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}
.exec-consent--granted    { background: #C8E6C9; color: #2E7D32; }
.exec-consent--denied     { background: #FFCDD2; color: #C62828; }
.exec-consent--requested  { background: #FFF9C4; color: #F57F17; }
.exec-consent--not_requested { background: #F5F5F5; color: #757575; }
.exec-ok  { color: #2E7D32; font-weight: 700; }
.exec-off { color: #9E9E9E; }
.exec-restriction { color: #E65100; font-weight: 600; }
.executive-etapa5-impact { margin-top: 0.75rem; }
.executive-impact-title { font-size: 0.72rem; font-weight: 700; color: #6A1B9A; margin-bottom: 0.4rem; text-transform: uppercase; }
.exec-impact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem;
}
.exec-impact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  font-size: 0.73rem;
  color: #37474F;
}
.exec-impact-icon { font-size: 1rem; flex-shrink: 0; margin-top: 0.05rem; }


/* ═══════════════════════════════════════════════════════════════════════════
   PAYMENT PANEL — Etapa 9
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Client-side Payment Panel ─────────────────────────────────────────────── */
.payment-panel-client {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  margin: 0.75rem 0;
}
.payment-panel-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.payment-panel-icon { font-size: 1.5rem; }
.payment-panel-title { font-weight: 700; font-size: 0.95rem; color: #1e293b; margin: 0; }
.payment-badge {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
}
.payment-badge--demo { background: #fef3c7; color: #92400e; }
.payment-badge--approved { background: #d1fae5; color: #065f46; }
.payment-kit-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.75rem;
}
.payment-kit-name { font-weight: 600; font-size: 0.9rem; color: #1e293b; }
.payment-kit-price { font-weight: 700; font-size: 1rem; color: #16a34a; }
.payment-btn { width: 100%; margin-bottom: 0.5rem; }
.payment-pending-block { display: flex; flex-direction: column; gap: 0.5rem; }
.payment-status-label { font-size: 0.85rem; color: #64748b; margin: 0.25rem 0; }
.payment-approved-block { padding: 0.75rem; background: #d1fae5; border-radius: 8px; }
.payment-approved-message { font-size: 0.85rem; color: #065f46; margin-top: 0.5rem; }
.payment-error { color: #dc2626; font-size: 0.82rem; margin-top: 0.5rem; }
.payment-disclaimer {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.75rem;
  text-align: center;
  font-style: italic;
}

/* ── Dev Payment Panel ─────────────────────────────────────────────────────── */
.dev-payment-panel {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 0.5rem;
}
.dev-panel-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #424242;
  margin-bottom: 0.5rem;
}
.dev-panel-empty { font-size: 0.8rem; color: #9e9e9e; }
.dev-panel-hint { font-size: 0.75rem; color: #bdbdbd; }
.dev-panel-empty-text { font-size: 0.8rem; color: #9e9e9e; margin-bottom: 0.25rem; }
.dev-payment-grid { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.5rem; }
.dev-payment-row { display: flex; gap: 0.5rem; font-size: 0.78rem; }
.dev-payment-key { color: #757575; min-width: 80px; flex-shrink: 0; }
.dev-payment-val { color: #212121; font-weight: 500; }
.dev-payment-id { font-family: monospace; font-size: 0.72rem; }
.dev-payment-events { margin-top: 0.5rem; }
.dev-payment-events-title { font-size: 0.75rem; font-weight: 700; color: #616161; margin-bottom: 0.25rem; }
.dev-payment-event { display: flex; justify-content: space-between; font-size: 0.73rem; color: #757575; }
.dev-payment-event-type { font-family: monospace; }
.dev-payment-event-at { color: #bdbdbd; }
.dev-payment-n8n { margin-top: 0.5rem; padding: 0.4rem 0.5rem; background: #f3e5f5; border-radius: 6px; }
.dev-payment-n8n-title { font-size: 0.72rem; font-weight: 700; color: #7b1fa2; margin-bottom: 0.2rem; }
.dev-payment-n8n-action { font-size: 0.75rem; color: #6a1b9a; }
.dev-payment-security { margin-top: 0.5rem; }
.dev-payment-security-note { font-size: 0.72rem; color: #78909c; font-style: italic; }

/* ── Etapa 12: Animações suaves entre modos ─────────────────────────────── */
.view-fade {
  animation: viewFadeIn 0.35s ease-out;
}

@keyframes viewFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fade nas abas do chatbot (Sales <-> FAQ) */
.cs-chat-column > .cs-chat,
.cs-chat-column > div > .cs-chat {
  animation: chatTabFade 0.25s ease-out;
}

@keyframes chatTabFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ── Etapa 12: Badge LLM vs Determinístico ─────────────────────────────── */
.response-source-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 6px;
  margin-top: 4px;
  font-weight: 600;
}
.response-source-badge--llm {
  background: #d1fae5;
  color: #065f46;
}
.response-source-badge--deterministic {
  background: #fef3c7;
  color: #92400e;
}
.response-source-badge--fallback {
  background: #e0e7ff;
  color: #3730a3;
}

/* =============================================================================
   ============================================================================
   REDESIGN 2026-05-16 — Site do Cliente (ClientSiteView)
   Senior pass: legibilidade, hierarquia, proporção, acento quente.
   Estes blocos VENCEM os anteriores pela ordem do CSS.
   ============================================================================
   ============================================================================= */

/* ── Tokens estendidos: acentos quentes para apetite visual ──────────────── */
.cs-site {
  --rl-mostarda:      #F2A23B;   /* Acento quente principal — apetite */
  --rl-mostarda-dark: #D97F0F;   /* hover/cta */
  --rl-mostarda-bg:   #FFF4E1;   /* fundo suave */
  --rl-coral:         #E8744A;   /* destaque secundário */
  --rl-creme:         #FAF7F0;   /* fundo alternativo, sensação de pão */
  --rl-verde:         #2E7D32;
  --rl-verde-mid:     #43A047;
  --rl-verde-soft:    #66BB6A;
  --rl-verde-pale:    #C8E6C9;
  --rl-verde-bg:      #F1F8E9;
  --rl-ink:           #1B2A1F;   /* preto-verde para texto */
  --rl-ink-2:         #4E5A50;   /* secundário */
  --rl-line:          #E3E8DE;   /* separadores */
  --rl-page:          #FAFBF6;   /* fundo de página */
  --rl-white:         #FFFFFF;
  --rl-whatsapp:      #25D366;
  --rl-whatsapp-dark: #1EBA56;

  --rl-shadow-1: 0 1px 2px rgba(27,42,31,.05), 0 1px 3px rgba(27,42,31,.05);
  --rl-shadow-2: 0 4px 12px rgba(27,42,31,.08), 0 2px 4px rgba(27,42,31,.04);
  --rl-shadow-3: 0 12px 32px rgba(27,42,31,.12), 0 4px 8px rgba(27,42,31,.06);
  --rl-shadow-hero: 0 18px 48px rgba(46,125,50,0.28);

  /* Tipografia em escala 1.25 a partir de 16px */
  --rl-fs-xs:   0.8125rem;   /* 13px — labels */
  --rl-fs-sm:   0.9375rem;   /* 15px — corpo pequeno */
  --rl-fs-md:   1rem;        /* 16px — corpo */
  --rl-fs-lg:   1.125rem;    /* 18px — destaque */
  --rl-fs-xl:   1.375rem;    /* 22px — sub-headings */
  --rl-fs-2xl:  1.75rem;     /* 28px — section titles */
  --rl-fs-3xl:  2.25rem;     /* 36px — H1 desktop */
  --rl-fs-hero: clamp(2rem, 5vw, 3rem);

  /* Override base do font-size pra esse subtree */
  font-size: 16px;
  color: var(--rl-ink);
  background: var(--rl-page);

  max-width: 1240px;
  margin: 0 auto;
  padding: 0 1.25rem 4rem;
}

.cs-site *, .cs-site *::before, .cs-site *::after { box-sizing: border-box; }

/* ── HERO: imponente, com camadas e respiração ───────────────────────────── */
.cs-site .cs-hero {
  position: relative;
  background:
    radial-gradient(1100px 380px at 80% -20%, rgba(242,162,59,0.22) 0%, transparent 60%),
    radial-gradient(900px 340px at 0% 110%, rgba(102,187,106,0.32) 0%, transparent 60%),
    linear-gradient(135deg, #1B5E20 0%, #2E7D32 55%, #43A047 100%);
  border-radius: 28px;
  padding: 4rem 2.5rem 4.25rem;
  text-align: center;
  margin: 1rem 0 2.5rem;
  box-shadow: var(--rl-shadow-hero);
  overflow: hidden;
  isolation: isolate;
}
.cs-site .cs-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 12% 38%, rgba(255,255,255,0.06) 0 8px, transparent 9px),
    radial-gradient(circle at 88% 72%, rgba(255,255,255,0.05) 0 12px, transparent 13px),
    radial-gradient(circle at 70% 18%, rgba(255,255,255,0.04) 0 6px, transparent 7px);
  z-index: 0;
}
.cs-site .cs-hero-inner {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}
.cs-site .cs-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255,255,255,0.16);
  border: 1px solid rgba(255,255,255,0.32);
  color: #F1F8E9;
  font-size: var(--rl-fs-sm);
  padding: 0.45rem 1.1rem;
  border-radius: 999px;
  margin: 0;
  letter-spacing: 0.02em;
  font-weight: 600;
  backdrop-filter: blur(4px);
}
.cs-site .cs-hero-title {
  font-size: var(--rl-fs-hero);
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
  margin: 0;
  letter-spacing: -0.02em;
  text-wrap: balance;
}
.cs-site .cs-hero-subtitle {
  font-size: clamp(1rem, 1.6vw, 1.15rem);
  color: rgba(255,255,255,0.92);
  line-height: 1.6;
  margin: 0;
  max-width: 580px;
  text-wrap: pretty;
}
.cs-site .cs-hero .cs-btn--hero {
  background: var(--rl-mostarda);
  color: #2B1A00;
  font-size: var(--rl-fs-md);
  padding: 0.95rem 2rem;
  border-radius: 999px;
  font-weight: 800;
  letter-spacing: 0.01em;
  box-shadow: 0 6px 16px rgba(242,162,59,0.40);
  border: none;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
}
.cs-site .cs-hero .cs-btn--hero:hover {
  background: var(--rl-mostarda-dark);
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(217,127,15,0.45);
}

/* Trust strip abaixo do hero (gerado via ::after no inner) */
.cs-site .cs-hero-inner::after {
  content: "🌿 Recife · 🚚 Entrega local · 🥗 Comida leve, prática e saudável";
  display: inline-block;
  margin-top: 0.5rem;
  font-size: var(--rl-fs-xs);
  color: rgba(255,255,255,0.78);
  letter-spacing: 0.04em;
  font-weight: 500;
}

/* ── GRID PRINCIPAL: proporção 1.1fr 1fr no desktop ──────────────────────── */
.cs-site .cs-main-grid {
  display: grid;
  grid-template-columns: minmax(360px, 1.05fr) minmax(0, 1.15fr);
  gap: 2rem;
  margin-bottom: 2.5rem;
  align-items: start;
}
@media (max-width: 980px) {
  .cs-site .cs-main-grid { grid-template-columns: 1fr; gap: 1.5rem; }
}

.cs-site .cs-column-title {
  font-size: var(--rl-fs-lg);
  font-weight: 800;
  color: var(--rl-verde);
  margin: 0 0 0.85rem;
  letter-spacing: -0.01em;
}

/* ── CHAT: card mais arejado ──────────────────────────────────────────────── */
.cs-site .cs-chat {
  background: var(--rl-white);
  border-radius: 20px;
  border: 1px solid var(--rl-line);
  overflow: hidden;
  box-shadow: var(--rl-shadow-2);
}
.cs-site .cs-chat-header {
  background: linear-gradient(135deg, var(--rl-verde) 0%, #1B5E20 100%);
  color: #fff;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
}
.cs-site .cs-chat-avatar {
  font-size: 1.6rem;
  background: rgba(255,255,255,0.18);
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
}
.cs-site .cs-chat-name { font-weight: 700; font-size: var(--rl-fs-md); color: #fff; margin: 0; }
.cs-site .cs-chat-status { font-size: var(--rl-fs-xs); color: #C8E6C9; margin: 2px 0 0; }

.cs-site .cs-chat-messages {
  padding: 1.25rem;
  min-height: 280px;
  max-height: 460px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--rl-creme);
  scrollbar-width: thin;
}
.cs-site .cs-bubble {
  max-width: 82%;
  padding: 0.75rem 1rem;
  border-radius: 14px;
  font-size: var(--rl-fs-sm);
  line-height: 1.55;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
.cs-site .cs-bubble--bot {
  background: #fff;
  color: var(--rl-ink);
  align-self: flex-start;
  border: 1px solid var(--rl-line);
  border-bottom-left-radius: 4px;
  box-shadow: var(--rl-shadow-1);
}
.cs-site .cs-bubble--user {
  background: var(--rl-verde);
  color: #fff;
  align-self: flex-end;
  border-bottom-right-radius: 4px;
}

.cs-site .cs-chat-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-top: 1px solid var(--rl-line);
  background: #FBFCF8;
}
.cs-site .cs-quick-btn {
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  border: 1.5px solid var(--rl-verde-pale);
  background: #fff;
  color: var(--rl-verde);
  font-size: var(--rl-fs-xs);
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  transition: all 0.15s;
  white-space: nowrap;
}
.cs-site .cs-quick-btn:hover {
  background: var(--rl-verde-bg);
  border-color: var(--rl-verde-mid);
  transform: translateY(-1px);
}

.cs-site .cs-chat-input-row {
  display: flex;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-top: 1px solid var(--rl-line);
  background: #fff;
}
.cs-site .cs-chat-input {
  flex: 1;
  padding: 0.7rem 1rem;
  border: 1.5px solid var(--rl-line);
  border-radius: 12px;
  font-size: var(--rl-fs-md);
  font-family: inherit;
  outline: none;
  transition: border-color .15s;
  min-width: 0;
}
.cs-site .cs-chat-input:focus { border-color: var(--rl-verde-mid); box-shadow: 0 0 0 3px rgba(67,160,71,0.15); }

.cs-site .cs-btn--send {
  background: var(--rl-verde);
  color: #fff;
  padding: 0.7rem 1.25rem;
  border-radius: 12px;
  font-size: var(--rl-fs-sm);
  font-weight: 700;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  transition: background .15s;
}
.cs-site .cs-btn--send:hover { background: var(--rl-verde-mid); }

/* ── SEÇÕES: espaçamento generoso ─────────────────────────────────────────── */
.cs-site .cs-section {
  padding: 2.5rem 0;
  border-top: 1px solid var(--rl-line);
}
.cs-site .cs-section--alt {
  background: linear-gradient(180deg, #F4F8EE 0%, var(--rl-verde-bg) 100%);
  border-radius: 24px;
  padding: 2.5rem 2rem;
  margin: 1.25rem 0;
  border-top: none;
}
.cs-site .cs-section-title {
  font-size: var(--rl-fs-2xl);
  font-weight: 800;
  color: var(--rl-verde);
  margin: 0 0 1.5rem;
  letter-spacing: -0.02em;
  position: relative;
  padding-left: 1rem;
}
.cs-site .cs-section-title::before {
  content: "";
  position: absolute;
  left: 0; top: 12%; bottom: 12%;
  width: 5px;
  background: var(--rl-mostarda);
  border-radius: 3px;
}

/* ── KITS: cards generosos com sensação de produto ───────────────────────── */
.cs-site .cs-kits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.25rem;
}
.cs-site .cs-kit-card {
  background: var(--rl-white);
  border-radius: 18px;
  padding: 1.5rem 1.4rem 1.4rem;
  border: 1px solid var(--rl-line);
  box-shadow: var(--rl-shadow-1);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
.cs-site .cs-kit-card::before {
  content: "";
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--rl-verde) 0%, var(--rl-mostarda) 100%);
  opacity: 0;
  transition: opacity .2s ease;
}
.cs-site .cs-kit-card:hover {
  box-shadow: var(--rl-shadow-3);
  border-color: var(--rl-verde-pale);
  transform: translateY(-4px);
}
.cs-site .cs-kit-card:hover::before { opacity: 1; }
.cs-site .cs-kit-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  width: 56px; height: 56px;
  background: var(--rl-verde-bg);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
}
.cs-site .cs-kit-name {
  font-weight: 800;
  font-size: var(--rl-fs-lg);
  margin: 0 0 0.35rem;
  color: var(--rl-ink);
  letter-spacing: -0.01em;
  line-height: 1.25;
}
.cs-site .cs-kit-uso {
  font-size: var(--rl-fs-xs);
  color: var(--rl-ink-2);
  margin: 0 0 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}
.cs-site .cs-kit-composicao {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  font-size: var(--rl-fs-sm);
  line-height: 1.6;
  color: var(--rl-ink);
  flex: 1;
}
.cs-site .cs-kit-composicao li {
  padding: 0.25rem 0;
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.cs-site .cs-kit-composicao li::before {
  content: "✓";
  color: var(--rl-verde-mid);
  font-weight: 700;
  flex-shrink: 0;
}
.cs-site .cs-kit-cal {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: var(--rl-fs-xs);
  color: var(--rl-mostarda-dark);
  background: var(--rl-mostarda-bg);
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  margin: 0 0 1rem;
  font-weight: 700;
  align-self: flex-start;
}
.cs-site .cs-btn--kit {
  background: var(--rl-verde);
  color: #fff;
  font-size: var(--rl-fs-sm);
  width: 100%;
  margin-top: auto;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .15s;
}
.cs-site .cs-btn--kit:hover {
  background: var(--rl-verde-mid);
  transform: translateY(-1px);
}

/* ── PROMOS: layout em cards quentes ──────────────────────────────────────── */
.cs-site .cs-promos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}
.cs-site .cs-promo-card {
  background: var(--rl-white);
  border-radius: 18px;
  padding: 1.5rem;
  border: 1px solid var(--rl-line);
  box-shadow: var(--rl-shadow-1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.65rem;
  transition: all 0.2s;
  position: relative;
}
.cs-site .cs-promo-card:hover {
  box-shadow: var(--rl-shadow-3);
  transform: translateY(-3px);
  border-color: var(--rl-verde-pale);
}
.cs-site .cs-promo-card--destaque {
  background: linear-gradient(135deg, var(--rl-mostarda-bg) 0%, #FFFCF4 100%);
  border-color: var(--rl-mostarda);
  border-width: 2px;
}
.cs-site .cs-promo-icon {
  font-size: 2rem;
  width: 52px; height: 52px;
  background: var(--rl-verde-bg);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
}
.cs-site .cs-promo-card--destaque .cs-promo-icon {
  background: rgba(242,162,59,0.18);
}
.cs-site .cs-promo-badge {
  position: absolute;
  top: 1rem; right: 1rem;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background: var(--rl-mostarda);
  color: #2B1A00;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.cs-site .cs-promo-titulo {
  font-weight: 800;
  font-size: var(--rl-fs-lg);
  color: var(--rl-ink);
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.cs-site .cs-promo-desc {
  font-size: var(--rl-fs-sm);
  color: var(--rl-ink-2);
  line-height: 1.55;
  flex: 1;
  margin: 0;
}
.cs-site .cs-btn--promo {
  background: transparent;
  border: 2px solid var(--rl-verde);
  color: var(--rl-verde);
  font-size: var(--rl-fs-sm);
  padding: 0.6rem 1.25rem;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  transition: all .15s;
  margin-top: 0.25rem;
}
.cs-site .cs-btn--promo:hover {
  background: var(--rl-verde);
  color: #fff;
}

/* ── INDICAÇÃO: banner ─────────────────────────────────────────────────────── */
.cs-site .cs-indicacao-card {
  background: linear-gradient(135deg, #FFF7E6 0%, var(--rl-mostarda-bg) 100%);
  border-radius: 20px;
  padding: 1.75rem 2rem;
  border: 2px solid var(--rl-mostarda);
  box-shadow: var(--rl-shadow-2);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  max-width: 100%;
}
.cs-site .cs-indicacao-icon {
  font-size: 3.25rem;
  flex-shrink: 0;
}
.cs-site .cs-indicacao-title {
  font-weight: 800;
  font-size: var(--rl-fs-xl);
  margin: 0 0 0.35rem;
  color: var(--rl-ink);
  letter-spacing: -0.01em;
}
.cs-site .cs-indicacao-desc {
  font-size: var(--rl-fs-md);
  color: var(--rl-ink-2);
  margin: 0 0 1rem;
  line-height: 1.55;
}
.cs-site .cs-btn--indicar {
  background: var(--rl-mostarda);
  color: #2B1A00;
  padding: 0.7rem 1.5rem;
  border-radius: 999px;
  font-weight: 800;
  font-size: var(--rl-fs-sm);
  border: none;
  cursor: pointer;
  transition: all .15s;
  box-shadow: 0 4px 12px rgba(242,162,59,0.3);
}
.cs-site .cs-btn--indicar:hover {
  background: var(--rl-mostarda-dark);
  color: #fff;
  transform: translateY(-2px);
}
@media (max-width: 600px) {
  .cs-site .cs-indicacao-card { flex-direction: column; text-align: center; padding: 1.5rem; }
}

/* ── CTA FINAL WHATSAPP ───────────────────────────────────────────────────── */
.cs-site .cs-cta-section {
  text-align: center;
  padding: 3.5rem 1.5rem;
  background: linear-gradient(180deg, transparent 0%, var(--rl-verde-bg) 100%);
  border-radius: 24px;
  margin-top: 2rem;
}
.cs-site .cs-cta-title {
  font-size: var(--rl-fs-2xl);
  font-weight: 800;
  color: var(--rl-verde);
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
}
.cs-site .cs-cta-subtitle {
  font-size: var(--rl-fs-md);
  color: var(--rl-ink-2);
  margin: 0 0 1.75rem;
  line-height: 1.55;
  max-width: 480px;
  margin-left: auto; margin-right: auto;
}
.cs-site .cs-btn--whatsapp {
  background: var(--rl-whatsapp);
  color: #fff;
  font-size: var(--rl-fs-lg);
  padding: 1rem 2.5rem;
  border-radius: 999px;
  font-weight: 800;
  text-decoration: none;
  display: inline-block;
  box-shadow: 0 8px 24px rgba(37,211,102,0.35);
  transition: all .15s;
}
.cs-site .cs-btn--whatsapp:hover {
  background: var(--rl-whatsapp-dark);
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(37,211,102,0.45);
}

/* ── CHECKOUT CARD: alinhado ao novo sistema ─────────────────────────────── */
.cs-site .cs-checkout-card {
  background: linear-gradient(135deg, #FFFEF7 0%, var(--rl-mostarda-bg) 100%);
  border: 2px solid var(--rl-mostarda);
  border-radius: 18px;
  padding: 1.25rem;
  margin: 1rem;
  box-shadow: var(--rl-shadow-2);
}
.cs-site .cs-checkout-title { font-size: var(--rl-fs-md); color: var(--rl-verde); }
.cs-site .cs-checkout-kit { font-size: var(--rl-fs-lg); }
.cs-site .cs-checkout-summary-text { font-size: var(--rl-fs-sm); color: var(--rl-ink); }
.cs-site .cs-checkout-disclaimer { font-size: var(--rl-fs-xs); }

/* ── BADGES ───────────────────────────────────────────────────────────────── */
.cs-site .cs-badge { font-size: var(--rl-fs-xs); padding: 0.25rem 0.7rem; font-weight: 700; }

/* ── BOTÕES CONFIRM/CONSENT (afinar) ─────────────────────────────────────── */
.cs-site .cs-btn--confirm-kit {
  background: var(--rl-verde);
  padding: 0.55rem 1.25rem;
  font-size: var(--rl-fs-sm);
  border-radius: 999px;
  font-weight: 700;
}
.cs-site .cs-consent-btn { font-size: var(--rl-fs-sm); padding: 0.6rem 1rem; }

/* ── ABAS DO CHATBOT (substitui o inline style do JSX via wrapper) ───────── */
.cs-site .cs-chat-column > div:first-child + div { /* container das abas */
  border-bottom: 1px solid var(--rl-line) !important;
}

/* ── FOOTER ───────────────────────────────────────────────────────────────── */
.cs-site footer {
  margin-top: 3rem !important;
  padding: 1.5rem 0 !important;
  color: var(--rl-ink-2) !important;
  font-size: var(--rl-fs-xs) !important;
  border-top: 1px solid var(--rl-line) !important;
}

/* ── RESPONSIVO ───────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .cs-site { padding: 0 1rem 3rem; }
  .cs-site .cs-hero { padding: 2.75rem 1.5rem 3rem; border-radius: 20px; }
  .cs-site .cs-section--alt { padding: 2rem 1.25rem; border-radius: 18px; }
  .cs-site .cs-section-title { font-size: 1.5rem; }
  .cs-site .cs-cta-section { padding: 2.5rem 1rem; }
  .cs-site .cs-btn--whatsapp { font-size: var(--rl-fs-md); padding: 0.9rem 1.75rem; width: 100%; max-width: 360px; }
  .cs-site .cs-chat-messages { max-height: 360px; }
}

/* ── ANIMAÇÃO sutil de entrada ───────────────────────────────────────────── */
.cs-site .cs-hero { animation: rlFadeUp .5s ease-out; }
.cs-site .cs-section { animation: rlFadeUp .5s ease-out both; }
.cs-site .cs-section:nth-of-type(2) { animation-delay: .05s; }
.cs-site .cs-section:nth-of-type(3) { animation-delay: .10s; }
@keyframes rlFadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .cs-site *, .cs-site *::before, .cs-site *::after {
    animation: none !important;
    transition: none !important;
  }
}
